import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { weeklyBriefService, type WeeklyBriefQuiz } from "@/services/weeklyBriefService";
import { toast } from "sonner";
import { T } from "@/i18n/T";

export function BriefQuiz({ briefId, quizzes }: { briefId: string; quizzes: WeeklyBriefQuiz[] }) {
  const [answered, setAnswered] = useState<Record<string, { idx: number; correct: boolean }>>({});

  useEffect(() => {
    weeklyBriefService.getMyAttempts(briefId).then(rows => {
      const map: typeof answered = {};
      rows.forEach((r: any) => { map[r.question_id] = { idx: r.selected_index, correct: r.is_correct }; });
      setAnswered(map);
    });
  }, [briefId]);

  const choose = async (q: WeeklyBriefQuiz, idx: number) => {
    if (answered[q.id]) return;
    const correct = idx === q.correct_index;
    setAnswered(prev => ({ ...prev, [q.id]: { idx, correct } }));
    try {
      const xp = await weeklyBriefService.submitAttempt(briefId, q.id, idx, correct);
      if (correct) toast.success(`Correct! +${xp} XP`);
      else toast(`Not quite. ${q.explanation}`);
    } catch (e: any) {
      if (String(e?.message).includes("Login")) toast("Sign in to earn XP");
    }
  };

  if (!quizzes.length) return null;

  return (
    <section className="mb-14">
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4"><T k="auto.quick-quiz-648f" /></div>
      <div className="space-y-5">
        {quizzes.map((q, i) => {
          const a = answered[q.id];
          return (
            <div key={q.id} className="rounded-2xl border border-border bg-card p-5">
              <div className="text-xs text-muted-foreground mb-2">Question {i + 1} of {quizzes.length}</div>
              <h3 className="font-display font-semibold text-foreground mb-4">{q.question}</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {q.options.map((opt, idx) => {
                  const isPicked = a?.idx === idx;
                  const isCorrect = a && idx === q.correct_index;
                  const state = !a ? "idle"
                    : isCorrect ? "correct"
                    : isPicked ? "wrong"
                    : "muted";
                  return (
                    <button
                      key={idx}
                      disabled={!!a}
                      onClick={() => choose(q, idx)}
                      className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors flex items-center justify-between gap-2
                        ${state === "idle" ? "border-border hover:border-primary/40 bg-background" : ""}
                        ${state === "correct" ? "border-success bg-success/10 text-success" : ""}
                        ${state === "wrong" ? "border-destructive bg-destructive/10 text-destructive" : ""}
                        ${state === "muted" ? "border-border bg-muted/30 text-muted-foreground" : ""}`}
                    >
                      <span>{opt}</span>
                      {state === "correct" && <Check className="h-4 w-4" />}
                      {state === "wrong" && <X className="h-4 w-4" />}
                    </button>
                  );
                })}
              </div>
              {a && (
                <p className="mt-3 text-sm text-muted-foreground">{q.explanation}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
